<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>{{ $t('teacher.title') }}</span>
          <div class="header-actions-wrapper">
            <div class="header-actions">
              <el-button 
                @click="handleRefresh"
                :icon="RefreshRight"
              >
                {{ $t('common.refresh') }}
              </el-button>
              <el-button 
                @click="phoneMaskEnabled = !phoneMaskEnabled"
                :icon="phoneMaskEnabled ? 'Hide' : 'View'"
                :type="phoneMaskEnabled ? 'warning' : 'info'"
                plain
                :title="phoneMaskEnabled ? $t('teacher.showPhone') : $t('teacher.maskPhone')"
              >
                {{ phoneMaskEnabled ? $t('teacher.masked') : $t('teacher.visible') }}
              </el-button>
              <el-dropdown @command="handleExportCommand" trigger="click">
                <el-button type="success">
                  <el-icon><Download /></el-icon>
                  {{ $t('common.export') }}
                  <el-icon class="el-icon--right"><ArrowDown /></el-icon>
                </el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="xlsx">
                      <el-icon><Document /></el-icon>
                      Export as Excel (.xlsx)
                    </el-dropdown-item>
                    <el-dropdown-item command="csv">
                      <el-icon><Tickets /></el-icon>
                      Export as CSV (.csv)
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>
        </div>
      </template>

      <!-- Search Bar -->
      <div class="search-bar">
        <div class="search-filters">
          <el-input 
            v-model="searchQuery" 
            :placeholder="$t('teacher.searchPlaceholder')" 
            clearable
            prefix-icon="Search"
            style="flex: 1; max-width: 400px;"
          />
          <el-select 
            v-model="statusFilter" 
            :placeholder="$t('teacher.allStatus')" 
            style="width: 150px; margin-left: 12px"
            clearable
          >
            <el-option :label="$t('teacher.allStatus')" value="" />
            <el-option :label="$t('teacher.statusOptions.active')" :value="1" />
            <el-option :label="$t('teacher.statusOptions.inactive')" :value="0" />
            <el-option :label="$t('teacher.statusOptions.deleted')" :value="2" />
            <el-option :label="$t('teacher.statusOptions.blocked')" :value="3" />
          </el-select>
          <div class="sort-wrapper" style="margin-left: 12px;">
            <el-select v-model="sortBy" :placeholder="$t('teacher.sortBy')" style="width: 120px;">
              <el-option :label="$t('teacher.sortByCreated')" value="createdAt" />
            </el-select>
            <el-button 
              @click="toggleSortOrder" 
              :icon="sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'"
              :title="sortOrder === 'asc' ? $t('teacher.sortAsc') : $t('teacher.sortDesc')"
              class="sort-toggle-btn"
            />
          </div>
        </div>
        
        <div class="view-toggle">
          <el-button-group>
            <el-button 
              :type="viewMode === 'table' ? 'primary' : ''" 
              @click="viewMode = 'table'"
            >
              <el-icon><List /></el-icon>
            </el-button>
            <el-button 
              :type="viewMode === 'card' ? 'primary' : ''" 
              @click="viewMode = 'card'"
            >
              <el-icon><Grid /></el-icon>
            </el-button>
          </el-button-group>
        </div>
      </div>

      <!-- Table View -->
      <el-table 
        v-if="viewMode === 'table'"
        :data="filteredTeachers" 
        v-loading="loading" 
        stripe
        style="width: 100%"
        :cell-style="{ padding: '12px 0' }"
      >
        <el-table-column type="index" width="50" />
        <el-table-column prop="avatar" :label="$t('teacher.avatar')" width="100">
          <template #default="{ row }">
            <ImagePreview 
              v-if="row.avatar" 
              :src="row.avatar" 
              width="50px" 
              height="50px"
              :round="true"
              :enable-preview="true"
            />
            <i v-else class="iconfont icon-teacher avatar-icon"></i>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$t('teacher.name')" width="150">
          <template #default="{ row }">
            <span>{{ row.name }}</span>
            <el-tooltip v-if="row.serviceAccountOpenId" :content="$t('teacher.subscribed')" placement="top" effect="light">
              <el-icon class="subscribed-icon" style="margin-left: 6px; color: #67c23a; font-size: 16px;"><Bell /></el-icon>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column prop="phone" :label="$t('teacher.phone')" width="140">
          <template #default="{ row }">
            {{ maskPhone(row.phone) }}
          </template>
        </el-table-column>
        <el-table-column prop="workExperienceYear" :label="$t('teacher.workExperience')" width="120" align="center">
          <template #default="{ row }">
            {{ row.workExperienceYear ? `${row.workExperienceYear} ${$t('teacher.years')}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="isVisibleForMiniApp" :label="$t('teacher.isVisibleForMiniApp')" min-width="180" align="center">
          <template #default="{ row }">
            <el-tag :type="row.isVisibleForMiniApp ? 'success' : 'info'" size="small">
              {{ row.isVisibleForMiniApp ? $t('teacher.showInMiniApp') : $t('teacher.hideFromMiniApp') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="status" :label="$t('teacher.status')" width="100" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'" size="small">
              {{ row.status === 1 ? $t('teacher.active') : $t('teacher.inactive') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column v-if="filteredTeachers.some(t => t.remark)" prop="remark" :label="$t('teacher.remark')" min-width="200">
          <template #default="{ row }">
            <span class="remark-text">{{ row.remark || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.actions')" width="200" fixed="right" align="center">
          <template #default="{ row }">
            <div class="action-buttons">
              <el-button type="primary" size="small" link @click="viewDetail(row)">
                {{ $t('common.edit') }}
              </el-button>
              <el-button type="danger" size="small" link @click="handleDelete(row)">
                {{ $t('common.delete') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- Card View -->
      <div v-else class="teacher-cards" v-loading="loading">
        <div v-if="filteredTeachers.length === 0" class="empty-state">
          <el-empty :description="$t('teacher.noTeachers')" />
        </div>
        <div v-else class="cards-grid">
          <div 
            v-for="teacher in filteredTeachers" 
            :key="teacher.id" 
            class="teacher-card"
          >
            <div class="card-header-section">
              <div class="teacher-avatar" @click="previewAvatar(teacher.avatar)">
                <ImagePreview 
                  v-if="teacher.avatar" 
                  :src="teacher.avatar" 
                  width="56px" 
                  height="56px"
                  :enable-preview="false"
                  fit="cover"
                  round
                />
                <i v-else class="iconfont icon-teacher avatar-icon"></i>
              </div>
              <div class="teacher-info">
                <h3 class="teacher-name">{{ teacher.name }}
                  <el-tooltip v-if="teacher.serviceAccountOpenId" :content="$t('teacher.subscribed')" placement="top" effect="light">
                    <el-icon class="subscribed-icon-inline"><Bell /></el-icon>
                  </el-tooltip>
                </h3>
                <a :href="`tel:${teacher.phone}`" class="teacher-phone">
                  <el-icon><Phone /></el-icon>
                  <span>{{ maskPhone(teacher.phone) }}</span>
                </a>
              </div>
              <div class="status-badge" :class="getStatusClass(teacher.status)">
                <el-icon v-if="teacher.status === 1"><CircleCheck /></el-icon>
                <el-icon v-else><InfoFilled /></el-icon>
                <span>{{ getStatusLabel(teacher.status) }}</span>
              </div>
            </div>
            
            <div class="card-body-section">
              <div class="info-row">
                <div class="info-label">
                  <el-icon><View /></el-icon>
                  <span>{{ $t('teacher.isVisibleForMiniApp') }}</span>
                </div>
                <el-tag 
                  :type="teacher.isVisibleForMiniApp ? 'success' : 'info'" 
                  size="small" 
                  effect="light"
                  round
                >
                  {{ teacher.isVisibleForMiniApp ? $t('teacher.showInMiniApp') : $t('teacher.hideFromMiniApp') }}
                </el-tag>
              </div>
            </div>

            <div v-if="teacher.remark" class="card-remark-section">
              <div class="remark-row">
                <el-icon class="remark-icon"><Tickets /></el-icon>
                <span class="remark-text">{{ teacher.remark }}</span>
              </div>
            </div>
            
            <div class="card-footer-section">
              <el-button type="primary" size="default" plain @click="viewDetail(teacher)">
                <el-icon><Edit /></el-icon>
                {{ $t('common.edit') }}
              </el-button>
              <el-button type="danger" size="default" plain @click="handleDelete(teacher)">
                <el-icon><Delete /></el-icon>
                {{ $t('common.delete') }}
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <el-empty v-if="!teacherStore.teachers.length && !loading" :description="$t('teacher.noTeachers')" />
    </el-card>

    <!-- Image Preview Dialog -->
    <el-dialog v-model="showImagePreview" title="Avatar Preview" width="500px" center>
      <div style="text-align: center;">
        <img :src="previewImageUrl" alt="Avatar Preview" style="max-width: 100%; max-height: 80vh; border-radius: 8px;" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed, onActivated } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useTeacherStore } from '@/stores/teacher'
import { ElMessage, ElMessageBox } from 'element-plus'
import { List, Grid, Phone, Edit, Delete, Search, Download, ArrowUp, ArrowDown, Document, Tickets, CircleCheck, InfoFilled, RefreshRight, Notebook, Hide, View, Bell } from '@element-plus/icons-vue'
import { exportToExcel, exportToCSV } from '@/utils/export'
import { imageCacheUtil } from '@/utils/image-cache'
import { usePhoneMask } from '@/composables/usePhoneMask'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const teacherStore = useTeacherStore()
const { phoneMaskEnabled, maskPhone } = usePhoneMask()

const loading = ref(false)
const exporting = ref(false)
const showImagePreview = ref(false)
const previewImageUrl = ref('')
const viewMode = ref(window.innerWidth < 768 ? 'card' : 'table') // 'table' or 'card'
const searchQuery = ref('')
const statusFilter = ref(1) // Default show active teachers only ('', 0=Inactive, 1=Active, 2=Deleted, 3=Blocked)
const sortBy = ref('createdAt') // Default sort by created time
const sortOrder = ref('desc') // Default sort descending (newest first)

const toggleSortOrder = () => {
  sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
}

// Auto-switch to card view on mobile
const checkMobileView = () => {
  if (window.innerWidth < 768) {
    viewMode.value = 'card'
  } else {
    viewMode.value = 'table'
  }
}

// Computed property for real-time filtering and sorting
const filteredTeachers = computed(() => {
  const filtered = teacherStore.teachers.filter(teacher => {
    // Status filter
    if (statusFilter.value !== '' && teacher.status !== statusFilter.value) {
      return false
    }
    
    // Search query filter (name or phone)
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase()
      const matchName = teacher.name?.toLowerCase().includes(query)
      const matchPhone = teacher.phone?.includes(query)
      if (!matchName && !matchPhone) {
        return false
      }
    }
    
    return true
  })
  
  // Sort by creation time
  return [...filtered].sort((a, b) => {
    const result = new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
    return sortOrder.value === 'asc' ? result : -result
  })
})

const fetchData = async (forceRefresh = false) => {
  loading.value = true
  await teacherStore.fetchTeachers({}, forceRefresh)
  loading.value = false
}

// Handle manual refresh - clear cache and reload
const handleRefresh = async () => {
  console.log('[Teacher List] Manual refresh...')
  
  // Clear cached image data
  imageCacheUtil.clearAllCachedImages()
  
  // Reload data from API
  await fetchData(true)
  
  ElMessage.success('Refreshed successfully')
}

onMounted(() => {
  // Load saved preferences
  const savedViewMode = localStorage.getItem('teacherViewMode')
  if (savedViewMode) {
    viewMode.value = savedViewMode
  }
  // If no saved preference, use the mobile-checking default set in ref initialization
  
  const savedStatusFilter = localStorage.getItem('teacherStatusFilter')
  if (savedStatusFilter !== null && savedStatusFilter !== '') {
    const parsedValue = JSON.parse(savedStatusFilter)
    // Only apply saved value if it's a valid status number (0, 1, 2, 3), not empty string
    if (parsedValue === 0 || parsedValue === 1 || parsedValue === 2 || parsedValue === 3) {
      statusFilter.value = parsedValue
    }
  }
  
  window.addEventListener('resize', checkMobileView)
  fetchData()
})

// Refresh data when component is activated (returning from edit/add page)
onActivated(() => {
  fetchData()
})

// Watch for route changes to refresh data when returning from other pages
watch(() => route.fullPath, () => {
  // Only refresh if we're on the teacher list page
  if (route.path === '/teacher') {
    fetchData()
  }
})

// Cleanup event listener and save preference
watch(() => viewMode.value, () => {
  localStorage.setItem('teacherViewMode', viewMode.value)
})

// Save status filter preference
watch(() => statusFilter.value, () => {
  localStorage.setItem('teacherStatusFilter', JSON.stringify(statusFilter.value))
})

const viewDetail = (row) => {
  router.push(`/teacher/detail/${row.id}`)
}

const previewAvatar = (avatarUrl) => {
  if (!avatarUrl) return
  previewImageUrl.value = avatarUrl
  showImagePreview.value = true
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(t('common.confirmDelete'), t('common.tip'), { type: 'warning' })
    // Update status to deleted (2) instead of actual deletion
    await teacherStore.modifyTeacher(row.id, { status: 2 })
    ElMessage.success(t('common.success'))
    fetchData()
  } catch (error) {
    if (error !== 'cancel') console.error(error)
  }
}

const getStatusLabel = (status) => {
  const statusMap = {
    0: t('teacher.statusOptions.inactive'),
    1: t('teacher.statusOptions.active'),
    2: t('teacher.statusOptions.deleted'),
    3: t('teacher.statusOptions.blocked')
  }
  return statusMap[status] || 'Unknown'
}

const getStatusTagType = (status) => {
  const typeMap = {
    0: 'info',
    1: 'success',
    2: 'danger',
    3: 'warning'
  }
  return typeMap[status] || 'info'
}

const getStatusClass = (status) => {
  const classMap = {
    0: 'status-inactive',
    1: 'status-active',
    2: 'status-deleted',
    3: 'status-blocked'
  }
  return classMap[status] || 'status-inactive'
}

const handleExportCommand = (format) => {
  if (format === 'csv') {
    handleExport('csv')
  } else {
    handleExport('xlsx')
  }
}

const handleExport = async (format = 'xlsx') => {
  try {
    exporting.value = true
    
    // Use filtered teachers from computed property (current table data)
    const teachers = filteredTeachers.value
    
    if (teachers.length === 0) {
      ElMessage.warning(t('common.noData'))
      return
    }
    
    // Transform data for export
    const exportData = teachers.map(teacher => ({
      'ID': teacher.id,
      'Name': teacher.name || '',
      'Phone': teacher.phone || '',
      'Status': teacher.status === 1 ? 'Active' : teacher.status === 0 ? 'Inactive' : teacher.status === 2 ? 'Deleted' : 'Blocked',
      'Visible in Mini App': teacher.isVisibleForMiniApp ? 'Yes' : 'No',
      'Created At': teacher.createTime ? new Date(teacher.createTime).toLocaleString() : ''
    }))
    
    // Export using utility based on format
    if (format === 'csv') {
      exportToCSV(exportData, 'teachers')
    } else {
      exportToExcel(exportData, 'teachers', 'Teachers')
    }
    
  } catch (error) {
    console.error('Export failed:', error)
    ElMessage.error(error.message || t('common.exportFailed'))
  } finally {
    exporting.value = false
  }
}

onMounted(fetchData)
</script>

<style scoped>
.page-container {
  padding: 12px;
  background-color: var(--bg-color-page, #f5f7fa);
  min-height: 100vh;
  transition: background-color 0.3s ease;
}

.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;
  flex-wrap: wrap;
}

.search-filters {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.view-toggle {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}

.header-actions-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-header span {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  transition: color 0.3s ease;
}

.amount {
  font-weight: 600;
  color: var(--el-color-primary);
  font-size: 14px;
}

/* Enhanced table styling */
:deep(.el-table) {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

:deep(.el-table__header-wrapper) {
  background: linear-gradient(to right, var(--bg-color-base, #f5f7fa), var(--bg-color-light, #eef1f6));
}

:deep(.el-table th) {
  font-weight: 600;
  color: var(--color-text-regular, #606266);
  padding: 12px 0;
  font-size: 13px;
  transition: color 0.3s ease;
}

:deep(.el-table__row) {
  transition: all 0.3s;
}

:deep(.el-table__row:hover) {
  background-color: var(--bg-color-light, #f5f7fa);
}

:deep(.el-table__row--striped) {
  td {
    background-color: var(--bg-color-base, #f5f7fa) !important;
    color: var(--color-text-primary, #303133) !important;
  }
  
  &:hover td {
    background-color: var(--bg-color-light, #f5f7fa) !important;
    color: var(--color-text-primary, #303133) !important;
  }
}

:deep(.el-table td) {
  padding: 12px 0;
  font-size: 13px;
  color: var(--color-text-primary, #303133);
}

/* Mobile-responsive table */
@media (max-width: 768px) {
  :deep(.el-table) {
    font-size: 12px;
  }
  
  :deep(.el-table .cell) {
    padding: 0 8px;
  }
  
  :deep(.el-button--small) {
    padding: 5px 8px;
    font-size: 12px;
  }
}

/* Status tag styling */
.status-tag {
  font-weight: 500;
}

/* Action buttons */
.action-buttons {
  display: flex;
  gap: 8px;
  justify-content: center;
}

/* Table Avatar Icon */
.avatar-icon {
  font-size: 40px;
  color: #303133;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
}

/* Card Grid View */
.teacher-cards {
  margin-top: 20px;
}

.teacher-cards .empty-state {
  padding: 40px 0;
}

.teacher-cards .cards-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 768px) {
  .teacher-cards .cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1200px) {
  .teacher-cards .cards-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

.teacher-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e4e7ed;
  overflow: hidden;
  transition: all 0.3s ease;
}

.teacher-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
  border-color: #409eff;
}

.teacher-card .card-header-section {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f5f7fa;
  position: relative;
  border-bottom: 1px solid #e4e7ed;
}

.teacher-card .teacher-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  overflow: hidden;
}

.teacher-card .teacher-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

/* Style ImagePreview component inside teacher-avatar */
.teacher-card .teacher-avatar :deep(.image-preview-wrapper),
.teacher-card .teacher-avatar :deep(.image-preview-container) {
  width: 100% !important;
  height: 100% !important;
  border-radius: 50%;
}

.teacher-card .teacher-avatar :deep(.preview-img) {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
}

.teacher-card .avatar-icon {
  font-size: 28px;
  color: #409eff;
}

.teacher-card .icon-teacher.avatar-icon {
  font-size: 30px;
  color: #409eff;
}

.teacher-card .avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.teacher-card .teacher-info {
  flex: 1;
  min-width: 0;
}

.teacher-card .teacher-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.teacher-card .teacher-phone {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #606266;
  font-size: 13px;
  text-decoration: none;
  transition: color 0.2s;
}

.teacher-card .teacher-phone:hover {
  color: #409eff;
}

.teacher-card .teacher-phone .el-icon {
  font-size: 14px;
}

.teacher-card .status-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 16px;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  border: 1px solid #e4e7ed;
}

.teacher-card .status-badge .el-icon {
  font-size: 14px;
}

.teacher-card .status-badge.status-active {
  color: #67c23a;
  background: rgba(103, 194, 58, 0.1);
  border-color: rgba(103, 194, 58, 0.3);
}

.teacher-card .status-badge.status-active .el-icon {
  color: #67c23a;
}

.teacher-card .status-badge.status-inactive {
  color: #909399;
  background: rgba(144, 147, 153, 0.1);
  border-color: rgba(144, 147, 153, 0.3);
}

.teacher-card .status-badge.status-inactive .el-icon {
  color: #909399;
}

.teacher-card .status-badge.status-deleted {
  color: #f56c6c;
  background: rgba(245, 108, 108, 0.1);
  border-color: rgba(245, 108, 108, 0.3);
}

.teacher-card .status-badge.status-deleted .el-icon {
  color: #f56c6c;
}

.teacher-card .status-badge.status-blocked {
  color: #e6a23c;
  background: rgba(230, 162, 60, 0.1);
  border-color: rgba(230, 162, 60, 0.3);
}

.teacher-card .status-badge.status-blocked .el-icon {
  color: #e6a23c;
}

.teacher-card .card-body-section {
  padding: 16px;
  border-bottom: 1px solid #f0f2f5;
}

.teacher-card .info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.teacher-card .info-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.teacher-card .info-label .el-icon {
  font-size: 16px;
  color: #909399;
}

.teacher-card .subscribed-icon-inline {
  font-size: 18px;
  color: #67c23a;
  flex-shrink: 0;
  margin-left: 6px;
}

.remark-text {
  font-size: 13px;
  color: #606266;
  white-space: normal;
  word-break: break-word;
  text-align: right;
  max-width: 200px;
}

.teacher-card .remark-row {
  margin-bottom: 12px;
}

.teacher-card .card-remark-section {
  padding: 12px 16px;
  background: #fafbfc;
}

.teacher-card .card-remark-section .remark-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 0;
}

.teacher-card .card-remark-section .remark-icon {
  font-size: 14px;
  color: #909399;
  flex-shrink: 0;
  margin-top: 2px;
}

.teacher-card .card-remark-section .remark-text {
  font-size: 12px;
  color: #606266;
  line-height: 1.4;
  word-break: break-word;
  text-align: left;
  max-width: none;
}

.teacher-card .card-footer-section {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  background: #f5f7fa;
  border-top: 1px solid #e4e7ed;
}

.teacher-card .card-footer-section .el-button {
  flex: 1;
  font-weight: 500;
}

/* Action buttons spacing in table */
.action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}


/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .page-container {
    padding: 8px;
  }

  .card-header {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .card-header span {
    font-size: 15px;
  }

  .header-actions-wrapper {
    width: 100%;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-end;
    flex-wrap: wrap;
  }

  .header-actions .el-button {
    flex-shrink: 0;
  }

  .search-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .search-filters {
    flex-direction: column;
    width: 100%;
    gap: 10px;
  }

  .search-filters .el-input {
    width: 100% !important;
    max-width: 100% !important;
  }

  .search-filters .el-select {
    width: 100% !important;
    margin-left: 0 !important;
  }

  .sort-wrapper {
    margin-left: 0 !important;
    width: 100%;
    display: flex;
    gap: 8px;
  }

  .sort-wrapper .el-select {
    flex: 1;
  }

  .view-toggle {
    width: 100%;
    justify-content: center;
  }

  .view-toggle .el-button-group {
    width: 100%;
    display: flex;
  }

  .view-toggle .el-button-group .el-button {
    flex: 1;
  }
  
  .teacher-cards .cards-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .teacher-card .card-header-section {
    padding: 12px;
    gap: 10px;
  }
  
  .teacher-card .teacher-avatar {
    width: 48px;
    height: 48px;
  }
  
  .teacher-card .avatar-icon {
    font-size: 24px;
  }
  
  .teacher-card .icon-teacher.avatar-icon {
    font-size: 26px;
  }
  
  .teacher-card .teacher-name {
    font-size: 14px;
  }
  
  .teacher-card .teacher-phone {
    font-size: 12px;
  }
  
  .teacher-card .status-badge {
    padding: 4px 8px;
    font-size: 11px;
  }
  
  .teacher-card .card-body-section {
    padding: 12px;
  }
  
  .teacher-card .info-label {
    font-size: 12px;
  }
  
  .teacher-card .card-footer-section {
    padding: 10px 12px;
    gap: 6px;
  }
  
  .teacher-card .card-footer-section .el-button {
    font-size: 12px;
    padding: 8px 12px;
  }
}

/* Small mobile devices */
@media (max-width: 480px) {
  .teacher-card .card-header-section {
    padding: 10px;
    gap: 8px;
  }
  
  .teacher-card .teacher-avatar {
    width: 44px;
    height: 44px;
  }
  
  .teacher-card .avatar-icon {
    font-size: 22px;
  }
  
  .teacher-card .icon-teacher.avatar-icon {
    font-size: 24px;
  }
  
  .teacher-card .teacher-name {
    font-size: 13px;
  }
  
  .teacher-card .teacher-phone {
    font-size: 11px;
  }
  
  .teacher-card .status-badge {
    padding: 3px 6px;
    font-size: 10px;
  }
  
  .teacher-card .card-body-section {
    padding: 10px;
  }
  
  .teacher-card .info-label {
    font-size: 11px;
  }
  
  .teacher-card .card-footer-section {
    padding: 8px 10px;
    gap: 6px;
  }
  
  .teacher-card .card-footer-section .el-button {
    font-size: 11px;
    padding: 6px 10px;
  }
}
</style>